import React, { memo } from 'react';
import { Tooltip } from '@mui/material';
import { styled } from '@mui/system';
import styles from '../../../assets/css/components/Aof.module.css';
import { IPlayerState } from '../../../Core/State/IPlayerState';
import { t } from '../../../i18n';

interface StatusBarProps {
    stateName: string | null;
    pot: number | null;
    playerEquities: {
        [key: string]: {
            playerId: string;
            equity: number;
        };
    };
    players: IPlayerState[];
    sidePots?: Array<{
        amount: number;
        eligiblePlayers: string[];
    }>;
    formatCurrency: (amount: number) => string;
    gameStartingIn?: number | null;
    isTableClosed?: boolean;
    isTournamentStarting?: boolean;
    tableReactionStatus?: TableReactionStatus | null;
}

export interface TableReactionStatus {
    id: string;
    sourceUsername: string;
    targetUsername: string;
    reactionLabel: string;
    reactionAssetUrl: string;
}

const InfoContainer = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    height: '100%',
    padding: '0 16px',
    fontFamily: '"Inter", sans-serif',
    margin: '0 auto',
    minWidth: 'fit-content',
});

const ShowdownText = styled('div')({
    color: '#F52740',
    fontFamily: '"Inter", sans-serif',
    fontWeight: 700,
    fontSize: '16px',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    backgroundImage: 'linear-gradient(90deg, #F52740 0%, #a0263c 50%, #F52740 100%)',
    backgroundSize: '200% auto',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    animation: 'shine 2s linear infinite',
    whiteSpace: 'nowrap',
    userSelect: 'none',
    textShadow: '0 0 10px rgba(245, 39, 64, 0.3)',
});

const PotInfo = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#ffd700',
    fontSize: '13px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    fontFamily: '"Inter", sans-serif',
    textShadow: '0 0 10px rgba(255, 215, 0, 0.3)',
    '& span': {
        color: '#ffffff',
        textShadow: '0 0 10px rgba(255, 255, 255, 0.3)',
    }
});

const TooltipContent = styled('div')({
    display: 'inline-block',
    cursor: 'help',
    fontFamily: '"Inter", sans-serif',
});

const AllInIndicator = styled('div')({
    display: 'flex',
    alignItems: 'center',
    padding: '2px 8px',
    borderRadius: '4px',
    backgroundColor: 'rgba(255, 0, 0, 0.15)',
    border: '1px solid rgba(255, 0, 0, 0.3)',
    color: '#ff4d4d',
    fontSize: '12px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    userSelect: 'none',
    fontFamily: '"Inter", sans-serif',
    textShadow: '0 0 10px rgba(255, 77, 77, 0.3)',
});

const Divider = styled('div')({
    width: '1px',
    height: '20px',
    background: 'rgba(255, 255, 255, 0.15)',
    margin: '0 12px',
    flexShrink: 0,
});

const EquityValue = styled('div')<{ equity: number }>(({ equity }) => ({
    display: 'inline-flex',
    padding: '2px 8px',
    borderRadius: '4px',
    backgroundColor: equity >= 80 ? 'rgba(0, 255, 0, 0.15)' :
                    equity >= 60 ? 'rgba(144, 238, 144, 0.15)' :
                    equity >= 40 ? 'rgba(255, 255, 0, 0.15)' :
                    equity >= 20 ? 'rgba(255, 165, 0, 0.15)' :
                    'rgba(255, 0, 0, 0.15)',
    border: `1px solid ${
        equity >= 80 ? 'rgba(0, 255, 0, 0.3)' :
        equity >= 60 ? 'rgba(144, 238, 144, 0.3)' :
        equity >= 40 ? 'rgba(255, 255, 0, 0.3)' :
        equity >= 20 ? 'rgba(255, 165, 0, 0.3)' :
        'rgba(255, 0, 0, 0.3)'
    }`,
    color: equity >= 80 ? '#00ff00' :
           equity >= 60 ? '#90ee90' :
           equity >= 40 ? '#ffff00' :
           equity >= 20 ? '#ffa500' :
           '#ff0000',
    fontSize: '12px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    userSelect: 'none',
    fontFamily: '"Inter", sans-serif',
    textShadow: `0 0 10px ${
        equity >= 80 ? 'rgba(0, 255, 0, 0.3)' :
        equity >= 60 ? 'rgba(144, 238, 144, 0.3)' :
        equity >= 40 ? 'rgba(255, 255, 0, 0.3)' :
        equity >= 20 ? 'rgba(255, 165, 0, 0.3)' :
        'rgba(255, 0, 0, 0.3)'
    }`,
}));

const PlayerName = styled('div')({
    fontSize: '12px',
    color: '#ffffff',
    marginRight: '4px',
    whiteSpace: 'nowrap',
    fontFamily: '"Inter", sans-serif',
    fontWeight: 500,
    textShadow: '0 0 10px rgba(255, 255, 255, 0.3)',
});

const GameStartingText = styled('div')({
    color: '#19e5e0',
    fontFamily: '"Inter", sans-serif',
    fontWeight: 700,
    fontSize: '16px',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    backgroundImage: 'linear-gradient(90deg, #19e5e0 0%, #0d7a77 50%, #19e5e0 100%)',
    backgroundSize: '200% auto',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    animation: 'shine 2s linear infinite',
    whiteSpace: 'nowrap',
    userSelect: 'none',
    textShadow: '0 0 10px rgba(25, 229, 224, 0.3)',
});

const ClosedContainer = styled('div')({
    color: '#19e5e0',
    fontFamily: '"Inter", sans-serif',
    fontWeight: 700,
    fontSize: '16px',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    backgroundImage: 'linear-gradient(90deg, #19e5e0 0%, #0d7a77 50%, #19e5e0 100%)',
    backgroundSize: '200% auto',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    animation: 'shine 2s linear infinite',
    whiteSpace: 'nowrap',
    userSelect: 'none',
    textShadow: '0 0 10px rgba(25, 229, 224, 0.3)',
});

const StartingContainer = styled(ClosedContainer)({});

const ReactionContainer = styled('div')({
    alignItems: 'center',
    color: '#ffffff',
    display: 'inline-flex',
    fontFamily: '"Inter", sans-serif',
    fontSize: '13px',
    fontWeight: 700,
    gap: '6px',
    lineHeight: 1,
    maxWidth: '100%',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
});

const ReactionName = styled('span')({
    color: '#19e5e0',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
});

const ReactionIcon = styled('img')({
    display: 'block',
    flex: '0 0 auto',
    height: '22px',
    objectFit: 'contain',
    width: '22px',
});

const StatusBar: React.FC<StatusBarProps> = ({
    stateName,
    pot,
    playerEquities,
    players,
    sidePots,
    formatCurrency,
    gameStartingIn,
    isTableClosed,
    isTournamentStarting,
    tableReactionStatus,
}) => {
    const [isVisible, setIsVisible] = React.useState(false);
    const [shouldRender, setShouldRender] = React.useState(false);
    const [countdown, setCountdown] = React.useState<number | null>(null);
    const [showdownPot, setShowdownPot] = React.useState<number | null>(null);
    const [showdownSidePots, setShowdownSidePots] = React.useState<typeof sidePots>(undefined);

    const prevStateRef = React.useRef<string | null>(null);
    const countdownEndAtRef = React.useRef<number | null>(null);
    const countdownSourceSecondsRef = React.useRef<number | null>(null);

    const allInPlayers = React.useMemo(
        () =>
            players.filter(
                (p) =>
                    p.balance !== null &&
                    p.availableActions === null &&
                    p.isActiveInCurrentGame &&
                    !p.hasFolded
            ),
        [players]
    );

    React.useEffect(() => {
        let intervalId: NodeJS.Timeout | null = null;
        let hideTimeout: NodeJS.Timeout | null = null;
        let renderTimeout: NodeJS.Timeout | null = null;

        const clearAllTimeouts = () => {
            if (intervalId) clearInterval(intervalId);
            if (hideTimeout) clearTimeout(hideTimeout);
            if (renderTimeout) clearTimeout(renderTimeout);
        };

        const hideStatusBar = () => {
            setIsVisible(false);
            renderTimeout = setTimeout(() => {
                setShouldRender(false);
                setCountdown(null);
                setShowdownPot(null);
                setShowdownSidePots(undefined);
                prevStateRef.current = null;
                countdownSourceSecondsRef.current = null;
            }, 400);
        };

        if (isTableClosed || isTournamentStarting) {
            clearAllTimeouts();
            hideStatusBar();
            countdownEndAtRef.current = null;
            countdownSourceSecondsRef.current = null;
            return () => {
                clearAllTimeouts();
            };
        }

        if (tableReactionStatus) {
            clearAllTimeouts();
            setCountdown(null);
            setShowdownPot(null);
            setShowdownSidePots(undefined);
            setShouldRender(true);
            renderTimeout = setTimeout(() => setIsVisible(true), 50);
            hideTimeout = setTimeout(() => {
                hideStatusBar();
            }, 2200);
            prevStateRef.current = null;
            countdownEndAtRef.current = null;
            countdownSourceSecondsRef.current = null;
        } else if (stateName === 'showdown') {
            clearAllTimeouts();
            setCountdown(null);
            setShouldRender(true);
            
            // Only set pot values when first entering showdown state
            if (prevStateRef.current !== 'showdown') {
                setShowdownPot(pot);
                setShowdownSidePots(sidePots);
            }
            
            hideTimeout = setTimeout(() => setIsVisible(true), 50);
            prevStateRef.current = 'showdown';
            countdownEndAtRef.current = null;
            countdownSourceSecondsRef.current = null;
        } else {
            const countdownSeconds = typeof gameStartingIn === 'number' && Number.isFinite(gameStartingIn)
                ? Math.ceil(gameStartingIn)
                : null;

            if (countdownSeconds !== null && countdownSeconds > 0) {
                if (
                    countdownSourceSecondsRef.current !== countdownSeconds ||
                    !countdownEndAtRef.current ||
                    Date.now() > countdownEndAtRef.current
                ) {
                    countdownEndAtRef.current = Date.now() + countdownSeconds * 1000;
                    countdownSourceSecondsRef.current = countdownSeconds;
                }

                setShouldRender(true);
                setIsVisible(true);

                const updateCountdown = () => {
                    const endAt = countdownEndAtRef.current!;
                    const msLeft = endAt - Date.now();
                    const remain = Math.ceil(msLeft / 1000);
                    
                    if (remain < 1) {
                        clearAllTimeouts();
                        countdownEndAtRef.current = null;
                        countdownSourceSecondsRef.current = null;
                        hideStatusBar();
                        return;
                    }
                    
                    setCountdown(remain);
                };

                // Initial draw
                updateCountdown();
                intervalId = setInterval(updateCountdown, 250);
            } else {
                clearAllTimeouts();
                countdownEndAtRef.current = null;
                countdownSourceSecondsRef.current = null;
                hideStatusBar();
            }
        }

        return () => {
            clearAllTimeouts();
        };
    }, [stateName, gameStartingIn, pot, sidePots, isTableClosed, isTournamentStarting, tableReactionStatus]);

    if (isTableClosed) {
        return (
            <div className={`${styles.statusBar} ${styles.statusBarShowdown}`} style={{ display: 'flex', justifyContent: 'center' }}>
                <InfoContainer>
                    <ClosedContainer>
                        {t('GameRoom:statusBar.tableClosed')}
                    </ClosedContainer>
                </InfoContainer>
            </div>
        );
    }

    if (isTournamentStarting) {
        return (
            <div className={`${styles.statusBar} ${styles.statusBarShowdown}`} style={{ display: 'flex', justifyContent: 'center' }}>
                <InfoContainer>
                    <StartingContainer>
                        {t('GameRoom:statusBar.tournamentStartingSoon')}
                    </StartingContainer>
                </InfoContainer>
            </div>
        );
    }

    if (!shouldRender) return null;

    const renderPotInfo = () => {
        const currentPot = showdownPot ?? pot;
        const currentSidePots = showdownSidePots ?? sidePots;
        
        if (!currentPot) return null;
        
        return (
            <PotInfo>
                {t('GameRoom:statusBar.mainPot')} <span>{formatCurrency(currentPot)}</span>
                {currentSidePots?.map((sidePot, index) => (
                    <Tooltip
                        key={`sidepot-${index}`}
                        title={t('GameRoom:statusBar.eligiblePlayers', { players: sidePot.eligiblePlayers.map(id =>
                            players.find(p => p.userId === id)?.username
                        ).join(', ') })}
                    >
                        <TooltipContent>
                            {t('GameRoom:statusBar.sidePot', { index: index + 1 })} <span>{formatCurrency(sidePot.amount)}</span>
                        </TooltipContent>
                    </Tooltip>
                ))}
            </PotInfo>
        );
    };

    const renderAllInPlayers = () => {
        if (!allInPlayers.length) return null;
        
        return (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {allInPlayers.map(player => (
                    <Tooltip
                        key={`allin-${player.userId}`}
                        title={t('GameRoom:statusBar.allInAmount', { amount: formatCurrency(player.balance || 0) })}
                    >
                        <TooltipContent>
                            <AllInIndicator>
                                {player.username} {t('GameRoom:statusBar.allIn')}
                            </AllInIndicator>
                        </TooltipContent>
                    </Tooltip>
                ))}
            </div>
        );
    };

    const renderEquities = () => {
        if (!playerEquities || Object.keys(playerEquities).length === 0) return null;

        return (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                {Object.values(playerEquities).map((equityData) => {
                    const player = players.find(p => p.userId === equityData.playerId);
                    if (!player) return null;

                    return (
                        <div key={`equity-${equityData.playerId}`} style={{ display: 'flex', alignItems: 'center' }}>
                            <PlayerName>
                                {player.username}:
                            </PlayerName>
                            <EquityValue equity={equityData.equity}>
                                {Math.round(equityData.equity)}%
                            </EquityValue>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className={`${styles.statusBar} ${isVisible ? styles.statusBarShowdown : ''}`} style={{ display: 'flex', justifyContent: 'center' }}>
            {tableReactionStatus ? (
                <InfoContainer>
                    <ReactionContainer title={`${tableReactionStatus.sourceUsername} threw ${tableReactionStatus.reactionLabel} to ${tableReactionStatus.targetUsername}`}>
                        <ReactionName>{tableReactionStatus.sourceUsername}</ReactionName>
                        <span>threw</span>
                        <ReactionIcon src={tableReactionStatus.reactionAssetUrl} alt={tableReactionStatus.reactionLabel} />
                        <span>to</span>
                        <ReactionName>{tableReactionStatus.targetUsername}</ReactionName>
                    </ReactionContainer>
                </InfoContainer>
            ) : countdown !== null && (
                <InfoContainer>
                    <GameStartingText>
                        {t('GameRoom:statusBar.gameStartingIn', { count: countdown })}
                    </GameStartingText>
                </InfoContainer>
            )}
            {/* <InfoContainer>
                {countdown !== null ? (
                    <GameStartingText>
                        GAME STARTING IN {countdown} SECONDS
                    </GameStartingText>
                ) : (
                    <>
                        <ShowdownText>
                            ♠️ SHOWDOWN! ♠️
                        </ShowdownText>
                        <Divider />
                        {renderPotInfo()}
                        {sidePots && sidePots.length > 0 && <Divider />}
                        {renderAllInPlayers()}
                        {playerEquities && Object.keys(playerEquities).length > 0 && <Divider />}
                        {renderEquities()}
                    </>
                )}
            </InfoContainer> */}
        </div>
    );
};

export default memo(StatusBar);
